import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Grid,
    MenuItem,
    IconButton,
    Divider
} from '@mui/material';
import { useEffect, useState } from 'react';
import { defaultBillingCommand, IBilling, IBillingDetail } from './models/Billing';

import CloseIcon from '@mui/icons-material/Close';

import useBillingService from './services/Billing';

import NumberFormat, { NumberFormatValues } from 'react-number-format';
import TextFieldRight from 'components/ui/TextFieldRight';
import { useRecoilValue } from 'recoil';
import { currentUserSessionAtom } from 'library/store';
import { useTranslation } from 'react-i18next';
import { Enum_ARTICLE_OPTION, Enum_CONSUMPTION_OPTION, IEnumerationItem } from 'features/configuration/models/Enumeration';


type Props = {
    open: boolean,
    billing: IBilling,
    onClose: () => void,
    onSave: (detail: IBilling) => void
};

export function EditBillingDiscountDialog({
    open,
    billing,
    onClose,
    onSave
}: Props) {

    const { t, i18n } = useTranslation();
    const { language: lg, taskCodes, userDescription, userName,
        employeeId, roleEntities, applicationSetup, userStores } = useRecoilValue(currentUserSessionAtom);

    const { sumBilling } = useBillingService();


    const [discount, setDiscount] = useState<number>(billing.discount);
    const [discountRate, setDiscountRate] = useState<number>(() => {

        const { initialNetAmount, discount, netAmount, amount } = billing;
        const billingCommand = billing.billingCommands.find(c => c.status === '95') ?? defaultBillingCommand;

        const calInitialNetAmount = sumBilling(billing, billingCommand, 'initialNetAmount');
        // Set corresponding rate

        //console.log({calInitialNetAmount, rate, })

        const rate = (calInitialNetAmount === 0) ? 0 : (discount / calInitialNetAmount) * 100;

        return rate;
    });

    useEffect(() => {
        if (!open) return;

        const billingCommand = billing.billingCommands.find(c => c.status === '95') ?? defaultBillingCommand;

        const calInitialNetAmount = sumBilling(billing, billingCommand, 'initialNetAmount');

        const rate = calInitialNetAmount === 0 ? 0 : (billing.discount / calInitialNetAmount) * 100;

        setDiscount(billing.discount);
        setDiscountRate(parseFloat(rate.toFixed(2)));

    }, [open, billing]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"

            PaperProps={{
                sx: {
                    // width: "50%",
                    //maxHeight: 600,
                    //height: height || '30vh',
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                }
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                }}
            >{billing.reference}
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

<Divider />

            <DialogContent sx={{
                overflowX: 'hidden',
                px: 3,      // controlled padding
            }}>
                <Stack spacing={2} mt={1} flexDirection={'column'}>

                    {/* QUANTITY + PRICE — SECOND LINE */}
                    <Grid container spacing={1} alignItems="center">
                        {/* QUANTITY WITH +/- */}
                        <Grid item xs={6}>

                            <NumberFormat
                                label={`${t('Rate')} ('%')`}
                                value={discountRate}
                                thousandSeparator={lg.includes('fr') ? ' ' : ','}
                                decimalScale={2}

                                isAllowed={(values: NumberFormatValues) => {
                                    return (values.floatValue || 0) >= 0 && (values.floatValue || 0) <= applicationSetup.maxBillingDiscountRate;
                                }}

                                allowNegative={false}
                                onValueChange={(v) => {
                                    const rate = v.floatValue || 0;
                                    setDiscountRate(rate);

                                    const billingCommand = billing.billingCommands.find(c => c.status === '95') ?? defaultBillingCommand;

                                    const calInitialNetAmount = sumBilling(billing, billingCommand, 'initialNetAmount');

                                    const discount = (rate / 100) * calInitialNetAmount;
                                    setDiscount(parseFloat(discount.toFixed(2)));
                                }
                                }
                                customInput={TextFieldRight}
                                size="small"
                                sx={{ width: 'calc(100% - 8px)' }}
                            />
                        </Grid>

                        {/* UNIT PRICE */}
                        <Grid item xs={6}>
                            <NumberFormat
                                label={t('Amount')}
                                value={discount}
                                thousandSeparator={lg.includes('fr') ? ' ' : ','}
                                decimalScale={2}

                                isAllowed={(values: NumberFormatValues) => {
                                    const billingCommand =
                                        billing.billingCommands.find(c => c.status === '95') ?? defaultBillingCommand;

                                    const calInitialNetAmount = sumBilling(billing, billingCommand, 'initialNetAmount');

                                    return (values.floatValue || 0) >= 0 &&
                                        (values.floatValue || 0) <= calInitialNetAmount * (applicationSetup.maxBillingDiscountRate / 100);
                                }}

                                allowNegative={false}
                                onValueChange={(v) => {
                                    setDiscount(v.floatValue || 0);
                                }
                                }
                                customInput={TextFieldRight}
                                size="small"
                                sx={{ width: 'calc(100% - 8px)' }}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>

            <Divider />

            {/* ACTIONS */}
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        onSave({ ...billing, discount });
                        onClose();
                    }
                    }
                >
                    {t('Confirm')}
                </Button>
            </DialogActions>
        </Dialog>

    );
}
